import { NAME } from '../common/constants';
import {
  CodecType,
  ConnectionState,
  IRemoteTrack,
  MediaType,
  MonitorEventId,
  MULTI_VIDEO_DATA_TYPE,
  RemoteStreamType,
  VideoCodec
} from '../enums';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';
import kvStatManager, { KV_REPORT_KEY_VIDEO_DECODE, KV_REPORT_KEY_VIDEO_ENCODE } from '../manager/kv-stat-manager';
import { getNetworkTime } from './time';

interface MonitorEvent {
  eventId: MonitorEventId;
  param1?: number;
  param2?: number;
  timestamp?: number;
  remoteUserId?: string;
  streamType?: MULTI_VIDEO_DATA_TYPE;
}
const eventHub = new Map<string, MonitorEvent[]>();
export const enum VideoCodecChange {
  SWITCH_SW,
  SWITCH_HW,
  START_SW,
  START_HW,
  CLOSE_CODEC
}
export const enum VideoCodecType {
  H264,
  H265,
  VP8
}
innerEmitter.on(INNER_EVENT.JOIN_SUCCESS, ({ room }) => {
  addMonitorEvent(room.userId, { eventId: MonitorEventId.JOIN });
});
innerEmitter.on(INNER_EVENT.LEAVE_START, ({ room }) => {
  addMonitorEvent(room.userId, { eventId: MonitorEventId.LEAVE });
});
innerEmitter.on(INNER_EVENT.LOCAL_TRACK_PUBLISHED, ({ track }) => {
  if (track.room) {
    let eventId: MonitorEventId = MonitorEventId.PUBLISH_AUDIO;
    if (track.mediaType === MediaType.BIG_VIDEO) {
      eventId = MonitorEventId.PUBLISH_VIDEO;
    } else if (track.mediaType === MediaType.AUX_VIDEO) {
      eventId = MonitorEventId.PUBLISH_AUX;
    }
    addMonitorEvent(track.room.userId, { eventId });
  }
});
innerEmitter.on(INNER_EVENT.LOCAL_TRACK_UNPUBLISHED, ({ track }) => {
  if (track.room) {
    let eventId: MonitorEventId = MonitorEventId.UNPUBLISH_AUDIO;
    if (track.mediaType === MediaType.BIG_VIDEO) {
      eventId = MonitorEventId.UNPUBLISH_VIDEO;
    } else if (track.mediaType === MediaType.AUX_VIDEO) {
      eventId = MonitorEventId.UNPUBLISH_AUX;
    }
    addMonitorEvent(track.room.userId, { eventId });
  }
});
innerEmitter.on(INNER_EVENT.TRACK_MUTED, ({ track }) => {
  if (track.room) {
    if (track.kind === NAME.AUDIO) {
      addMonitorEvent(track.room.userId, { eventId: track.isRemote ? MonitorEventId.MUTE_REMOTE_AUDIO : MonitorEventId.MUTE_AUDIO, remoteUserId: track.isRemote ? (track as IRemoteTrack).userId : undefined });
    } else {
      addMonitorEvent(track.room.userId, { eventId: track.isRemote ? MonitorEventId.MUTE_REMOTE_VIDEO : MonitorEventId.MUTE_VIDEO, remoteUserId: track.isRemote ? (track as IRemoteTrack).userId : undefined });
    }
  }
});
innerEmitter.on(INNER_EVENT.TRACK_UNMUTED, ({ track }) => {
  if (track.room) {
    if (track.kind === NAME.AUDIO) {
      addMonitorEvent(track.room.userId, { eventId: track.isRemote ? MonitorEventId.UNMUTE_REMOTE_AUDIO : MonitorEventId.UNMUTE_AUDIO, remoteUserId: track.isRemote ? (track as IRemoteTrack).userId : undefined });
    } else {
      addMonitorEvent(track.room.userId, { eventId: track.isRemote ? MonitorEventId.UNMUTE_REMOTE_VIDEO : MonitorEventId.UNMUTE_VIDEO, remoteUserId: track.isRemote ? (track as IRemoteTrack).userId : undefined });
    }
  }
});
innerEmitter.on(INNER_EVENT.REMOTE_TRACK_SUBSCRIBED, ({ track }) => {
  if (!track.room) return;
  if (track.mediaType === MediaType.AUDIO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.SUBSCRIBE_AUDIO, remoteUserId: track.userId });
  }
  if (track.mediaType === MediaType.BIG_VIDEO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.SUBSCRIBE_VIDEO, remoteUserId: track.userId });
  }
  if (track.mediaType === MediaType.SMALL_VIDEO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.SUBSCRIBE_SMALL_VIDEO, remoteUserId: track.userId });
  }
});
innerEmitter.on(INNER_EVENT.REMOTE_TRACK_UNSUBSCRIBED, ({ track }) => {
  if (!track.room) return;
  if (track.mediaType === MediaType.AUDIO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.UNSUBSCRIBE_AUDIO, remoteUserId: track.userId });
  }
  if (track.mediaType === MediaType.BIG_VIDEO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.UNSUBSCRIBE_VIDEO, remoteUserId: track.userId });
  }
  if (track.mediaType === MediaType.SMALL_VIDEO) {
    addMonitorEvent(track.room.userId, { eventId: MonitorEventId.UNSUBSCRIBE_SMALL_VIDEO, remoteUserId: track.userId });
  }
});
innerEmitter.on(INNER_EVENT.SWITCH_DEVICE_SUCCESS, ({ track }) => {
  if (track.room) {
    addMonitorEvent(track.room.userId, { eventId: track.kind === NAME.VIDEO ? MonitorEventId.SWITCH_CAMERA : MonitorEventId.SWITCH_MICROPHONE });
  }
});
innerEmitter.on(INNER_EVENT.LOCAL_TRACK_REPLACED, ({ track }) => {
  if (track.room) {
    addMonitorEvent(track.room.userId, { eventId: track.kind === NAME.VIDEO ? MonitorEventId.REPLACE_VIDEO : MonitorEventId.REPLACE_AUDIO });
  }
});
innerEmitter.on(INNER_EVENT.SIGNAL_CONNECTION_STATE_CHANGED, ({ room, prevState, state }) => {
  let eventId: MonitorEventId | undefined = undefined;
  switch (state) {
    case ConnectionState.CONNECTED:
      if (prevState === ConnectionState.RECONNECTING) {
        eventId = MonitorEventId.SIGNAL_RECONNECT_SUCCESS;
      } else {
        eventId = MonitorEventId.SIGNAL_CONNECTED;
      }
      break;
    case ConnectionState.DISCONNECTED:
      if (prevState === ConnectionState.RECONNECTING) {
        eventId = MonitorEventId.SIGNAL_RECONNECT_FAIL;
      } else {
        eventId = MonitorEventId.SIGNAL_DISCONNECTED;
      }
      break;
    case ConnectionState.RECONNECTING:
      eventId = MonitorEventId.SIGNAl_RECONNECTING;
      break;
  }
  if (eventId) {
    addMonitorEvent(room.userId, { eventId });
  }
});

innerEmitter.on(INNER_EVENT.PEER_CONNECTION_STATE_CHANGED, ({ room, prevState, state, remoteUserId }) => {
  const isRemote = !!remoteUserId;
  let eventId: MonitorEventId | undefined = undefined;
  switch (state) {
    case ConnectionState.CONNECTED:
      if (prevState === ConnectionState.RECONNECTING) {
        eventId = isRemote ? MonitorEventId.TRANSPORT_DOWNLINK_RECONNECT_SUCCESS : MonitorEventId.TRANSPORT_UPLINK_RECONNECT_SUCCESS;
      } else {
        eventId = isRemote ? MonitorEventId.TRANSPORT_DOWNLINK_CONNECTED : MonitorEventId.TRANSPORT_UPLINK_CONNECTED;
      }
      break;
    case ConnectionState.DISCONNECTED:
      if (prevState === ConnectionState.RECONNECTING) {
        eventId = isRemote ? MonitorEventId.TRANSPORT_DOWNLINK_RECONNECT_FAIL : MonitorEventId.TRANSPORT_UPLINK_RECONNECT_FAIL;
      }
      break;
    case ConnectionState.RECONNECTING:
      eventId = isRemote ? MonitorEventId.TRANSPORT_DOWNLINK_RECONNECTING : MonitorEventId.TRANSPORT_UPLINK_RECONNECTING;
      break;
  }
  if (eventId) {
    addMonitorEvent(room.userId, { eventId, remoteUserId });
  }
});

innerEmitter.on(INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED, ({ implementation, userId, remoteUserId, codec, isHWCodec, prevImplementation, streamType }) => {
  let param1 = isHWCodec ? VideoCodecChange.SWITCH_HW : VideoCodecChange.SWITCH_SW; // 0 切换软编/解、1 切换硬编/解、2 启动软编/解、3 启动硬编/解、4 关闭 codec
  if (!prevImplementation) {
    param1 = isHWCodec ? VideoCodecChange.START_HW : VideoCodecChange.START_SW;
  }
  const param2 = {
    H264: VideoCodecType.H264,
    H265: VideoCodecType.H265,
    VP8: VideoCodecType.VP8
  }[codec.toUpperCase()];
  const event: MonitorEvent = { eventId: MonitorEventId.VIDEO_ENCODER, param1, param2, streamType: streamType || MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG };
  if (remoteUserId) {
    event.remoteUserId = remoteUserId;
    event.eventId = MonitorEventId.VIDEO_DECODER;
  }
  addMonitorEvent(userId, event);

  kvStatManager.addEnum({ key: remoteUserId ? KV_REPORT_KEY_VIDEO_DECODE.DECODER_HW_SW : KV_REPORT_KEY_VIDEO_ENCODE.ENCDER_HW_SW, value: param1 });
  kvStatManager.addEnum({ key: remoteUserId ? KV_REPORT_KEY_VIDEO_DECODE.DECODER_TYPE : KV_REPORT_KEY_VIDEO_ENCODE.ENCODER_TYPE, value: param2 });
});

innerEmitter.on(INNER_EVENT.LOCAL_TRACK_RECAPTURE, ({ track, error }) => {
  if (track.userId) {
    const event: MonitorEvent = { eventId: MonitorEventId.DEVICE_CAPTURE, param1: 0 };
    if (track.kind === NAME.AUDIO) {
      event.streamType = MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUDIO;
      if (error) event.param1 = 2;
    } else {
      event.streamType = track.streamType === RemoteStreamType.Aux ? MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG : MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG;
      if (error) event.param1 = 8;
    }
    addMonitorEvent(track.userId, event);
  }
});


// add events to the hub
export function addMonitorEvent(userId: string, event: MonitorEvent) {
  const data = { ...event, timestamp: getNetworkTime() };
  if (eventHub.has(userId)) {
    eventHub.get(userId)!.push(data);
  } else {
    eventHub.set(userId, [data]);
  }
}

/**
 * 获取某个 userId 的所有的仪表盘事件。
 */
export function getEvents(userId: string) {
  if (eventHub.has(userId)) {
    const eventList = eventHub.get(userId)!.map(item => ({
      uint32_event_id: item.eventId,
      uint64_date: item.timestamp,
      str_userid: item.remoteUserId,
      uint32_param1: item.param1,
      uint32_param2: item.param2,
      uint32_video_stream_type: item.streamType
    }));
    eventHub.delete(userId);
    return eventList;
  }
  return [];
}
